import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import useServerStore from "../../store/server";
import { queryRecoveryBudget, routeOutcomeLog } from "./recoveryBudget";
import { pingBackend, startRecoveryPoll } from "./recoveryPoll";

// Automatic recovery after an outage (D-02). Renders nothing: the local
// QueryError views and the route error page already tell the user, so a global
// strip only duplicated them (and made the header jump while polling).
function ServerRecovery() {
  const unreachable = useServerStore((state) => state.unreachable);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    if (!unreachable) return;
    return startRecoveryPoll({
      ping: () => pingBackend(),
      onAlive: () => {
        const errored = queryClient.getQueryCache().findAll({
          type: "active",
          predicate: (query) => query.state.status === "error",
        });
        const allowed = new Set(
          queryRecoveryBudget.select(
            errored.map((query) => ({
              queryHash: query.queryHash,
              dataUpdatedAt: query.state.dataUpdatedAt,
            })),
            Date.now(),
          ),
        );
        // Clear first so a failure during the refetch raises the flag again.
        useServerStore.getState().setUnreachable(false);
        // Queries that already used their recovery refetches stay on their
        // QueryError (the user's "Försök igen" still works), which ends the
        // WR-04 loop.
        if (allowed.size > 0) {
          queryClient.refetchQueries({
            type: "active",
            predicate: (query) => allowed.has(query.queryHash),
          });
        }
      },
    });
  }, [unreachable, queryClient]);

  // Evidence that resets a route's recovery budget. Mounted once in the root
  // route, so it covers every route (including /course-editor) for the whole
  // session.
  useEffect(
    () =>
      router.subscribe("onResolved", (event) =>
        routeOutcomeLog.resolved(
          event.toLocation.pathname,
          router.state.matches.map((match) => match.status),
          Date.now(),
        ),
      ),
    [router],
  );

  return null;
}

export default ServerRecovery;
